import { createTheme, type Theme, ThemeProvider } from '@mui/material/styles';

const theme: Theme = createTheme({
    palette: {
        primary: {
            main: '#3678D7',
            light: '#2c60ac',
            dark: '#5990DE',
            contrastText: '#fff',
            P_50: '#F0F5FD',
            P_100: '#DCE7F5',
            P_450: '#7FAAE6',
            P_600: '#5990DE',
            P_750: '#3678D7',
            P_800_LIGHTER: '#A8C8EE',
        },
        success: {
            main: '#389224',
            contrastText: '#fff',
            S_50: '#ECF8EA',
            S_100: '#CDE8C7',
        },
        error: {
            main: '#962C27',
            light: '#962C27',
            dark: '#BC362F',
            contrastText: '#fff',
            E_50: '#F8EBEA',
            E_750: '#BC362F',
        },
        warning: {
            main: '#B5902D',
            contrastText: '#fff',
            W_50: '#FCF7EB',
        },
        text: {
            primary: '#16171A',
            secondary: '#7B7D80',
        },
        sidenav: {
            main: '#2F3032',
            contrastText: '#A8ACB2',
            contrastTextActive: '#FFFFFF',
            dividerBackground: '#7B7D80',
        },
        neutral: {
            black: '#16171A',
            white: '#FFFFFF',
            gray: '#7B7D80',
            lightGray: '#C5C8CC',
            borderColor: '#E0E3E5',
            darkBlue: '#153056',
            navy: '#163867',
            beige: '#FDE2C6',
            N_50: '#F4F6F8',
            N_100: '#E0E3E5',
            N_200: '#C5C8CC',
            N_300: '#A8ACB2',
            N_500: '#7B7D80',
            N_700: '#46494D',
            N_800: '#2F3032',
        },
    },
    typography: {
        // Customize typography settings (e.g., font family, sizes)
        fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: {
            fontSize: 24,
            fontWeight: 600,
            lineHeight: '32px',
        },
        h2: {
            fontSize: 20,
            fontWeight: 600,
            lineHeight: '24px',
        },
        h3: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '24px',
        },
        h4: {
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px',
        },
        body1: {
            fontSize: 16,
            fontWeight: 400,
            lineHeight: '24px',
            color: '#7B7D80',
        },
        body2: {
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            color: '#7B7D80',
        },
        body3: {
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '16px',
            color: '#7B7D80',
        },
        labelLarge: {
            fontSize: 16,
            fontWeight: 500,
            lineHeight: '16px',
            color: '#7B7D80',
        },
        labelMedium: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '16px',
            color: '#7B7D80',
        },
        labelSmall: {
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            color: '#7B7D80',
        },
    },
    customShadows: {
        default: '0px 4px 10px 0px rgba(47, 48, 50, 0.33)',
        modal: '0px 2px 16px 0px rgba(0, 0, 0, 0.16)',
        toast: '0px 4px 16px 0px rgba(0, 0, 0, 0.16)',
    },
    // Add more customizations (e.g., breakpoints, spacing, shadows)
    components: {
        // Inputs are 40px tall: MUI's small size. Unlike a fixed height, it lets multiline
        // fields and autocompletes with chips grow, and positions labels to match
        MuiTextField: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiFormControl: {
            defaultProps: {
                size: 'small',
            },
        },
        // Passes its own size to its TextField, and sizes its chips
        MuiAutocomplete: {
            defaultProps: {
                size: 'small',
            },
        },
        // For a Select outside a FormControl
        MuiSelect: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'primary' },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        fontSize: '14px',
                    }),
                },
                {
                    props: { variant: 'outlined' },
                    style: ({ theme }) => ({
                        color: theme.palette.primary.main,
                        fontSize: '14px',
                        '&:hover': {
                            backgroundColor: 'rgba(54, 120, 215, 0.08)',
                        },
                        '&:disabled': {
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            opacity: 0.349,
                        },
                    }),
                },
            ],
            styleOverrides: {
                contained: {
                    '&:disabled': {
                        backgroundColor: 'rgba(54,120,215, 0.35)',
                        color: '#FFFFFF',
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    background: '#F0F5FD',
                },
            },
        },
    },
});

export { ThemeProvider, theme };
